import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactStatusDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { useDeclaredFields } from '../../ui/useDeclaredFields.js'

/**
 * Adding one contact by hand.
 *
 * **The same path as an import of one row.** Adding somebody to a list is the
 * same assertion however it is done — that whoever added them holds their
 * consent — and it has to leave the same provenance behind. A second way in
 * would be a second place for that record to be written differently, and the
 * consent log is the whole reason this is a use case rather than an insert.
 *
 * Which is also why the subscription state and the segments travel with the
 * row rather than as calls after it: one transaction, so a contact cannot
 * exist without the audience somebody put them in.
 *
 * The declared fields are loaded here because the form asks for all of them,
 * and a form that offered only an address would be one nobody uses twice.
 */

export type AddOutcome =
  | { readonly status: 'idle' }
  | { readonly status: 'adding' }
  | { readonly status: 'added'; readonly email: string }
  /** Left exactly as they were, which includes whether they unsubscribed. */
  | { readonly status: 'already-there'; readonly email: string }
  | { readonly status: 'refused'; readonly detail: string }

export interface NewContactValues {
  readonly email: string
  readonly name: string
  readonly company: string
  readonly status: ContactStatusDto
  readonly segmentIds: readonly string[]
  readonly custom: Readonly<Record<string, string>>
}

/** What the form opens on. Subscribed, because that is why somebody is added. */
export const EMPTY_CONTACT: NewContactValues = {
  email: '',
  name: '',
  company: '',
  status: 'subscribed',
  segmentIds: [],
  custom: {},
}

export function useNewContact({
  client,
  onAdded,
}: {
  readonly client: ApiClient
  /** Told only when a row was genuinely inserted, so the board reloads once. */
  readonly onAdded: () => void
}) {
  const { fields } = useDeclaredFields({ client, subject: 'contacts' })

  const [outcome, setOutcome] = useState<AddOutcome>({ status: 'idle' })

  const add = useCallback(
    async (values: NewContactValues) => {
      const email = values.email.trim()
      setOutcome({ status: 'adding' })
      try {
        const added = await client.addContact(
          {
            email,
            name: values.name.trim(),
            company: values.company.trim(),
            status: values.status,
            custom: values.custom,
          },
          values.segmentIds,
        )
        setOutcome({ status: added ? 'added' : 'already-there', email })
        // Only on a real insert. An address that was already there changed
        // nothing, and reloading the board would suggest it had.
        if (added) onAdded()
      } catch (error) {
        setOutcome({
          status: 'refused',
          // The import path refuses an address the domain cannot parse, and
          // that reason is the useful thing to show rather than "try again".
          detail: error instanceof Error ? error.message : 'We could not add that contact.',
        })
      }
    },
    [client, onAdded],
  )

  const again = useCallback(() => setOutcome({ status: 'idle' }), [])

  return { fields, outcome, add, again }
}
